/**
 * EPIC-119 D3: "Lado inicial" / "Starting side". Two choices naming the home
 * team ("<HOME> ataca →" / "<HOME> ataca ←") for the FIRST half. Persisted by
 * the parent through `PATCH /matches/{id}/attack-direction`; reachable in
 * every match status (including completed, to correct a past match).
 */
import type { FC } from "react";

interface StartingSideControlProps {
  homeTeamName: string;
  /** `null`/`undefined` = never set, behaves as true (home attacks right). */
  homeAttacksRightFirstHalf: boolean | null | undefined;
  onChange: (homeAttacksRight: boolean) => void;
  t: (key: string, fallback?: string) => string;
}

const StartingSideControl: FC<StartingSideControlProps> = ({
  homeTeamName,
  homeAttacksRightFirstHalf,
  onChange,
  t,
}) => {
  const right = homeAttacksRightFirstHalf !== false;
  const label = (key: string, fallback: string) =>
    t(key, fallback).split("{{team}}").join(homeTeamName);
  const options: Array<{ value: boolean; testId: string; text: string }> = [
    {
      value: true,
      testId: "starting-side-home-right",
      text: label("startingSide.homeRight", "{{team}} attacks →"),
    },
    {
      value: false,
      testId: "starting-side-home-left",
      text: label("startingSide.homeLeft", "{{team}} attacks ←"),
    },
  ];
  return (
    <div
      data-testid="starting-side-control"
      role="group"
      aria-label={t("startingSide.label", "Starting side")}
      className="flex flex-wrap items-center gap-2"
    >
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-300">
        {t("startingSide.label", "Starting side")}
      </span>
      {options.map((option) => {
        const active = option.value === right;
        return (
          <button
            key={option.testId}
            type="button"
            data-testid={option.testId}
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            className={`px-3 py-1.5 max-lg:min-h-11 rounded-lg text-sm font-semibold border transition-colors ${
              active
                ? "text-emerald-100 border-emerald-400/60 bg-emerald-500/20"
                : "text-slate-300 border-slate-600 bg-slate-700 hover:bg-slate-600"
            }`}
          >
            {option.text}
          </button>
        );
      })}
    </div>
  );
};

export default StartingSideControl;
